import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { LineChart, Trophy, ShieldCheck, Wallet, Flame, ArrowRight, Brain, Activity } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Footer } from "../components/Footer";

const FEATURES = [
  { icon: Activity, title: "Daily 1–100 Scoring", desc: "Every stock scored from RSI, moving-average trend & momentum — recomputed once a day, post market-close." },
  { icon: Brain, title: "Why this pick?", desc: "Plain-English breakdowns so you learn what actually drives a score. No black boxes." },
  { icon: Trophy, title: "Honest Track Record", desc: "We store yesterday's picks and grade them vs the S&P 500. Transparent rolling hit-rate." },
  { icon: Wallet, title: "Risk-free Paper Trading", desc: "$100,000 virtual cash to practice the swing horizon without risking a cent." },
  { icon: Flame, title: "Streaks & Points", desc: "Rewarded for learning actions — check-ins and reading breakdowns. Never for 'winning'." },
  { icon: ShieldCheck, title: "Education First", desc: "Strictly a learning tool. Never real-money advice, with disclaimers everywhere." },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const go = () => navigate(user ? "/dashboard" : "/register");

  return (
    <div className="min-h-screen bg-background">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
            <LineChart className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-heading font-extrabold text-lg">Aqvoro</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate("/login")} data-testid="landing-signin">Sign in</Button>
          <Button onClick={go} data-testid="landing-cta-top" className="bg-primary text-primary-foreground hover:bg-primary/90">Get started</Button>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="ss-fade-up">
          <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-3 py-1 mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" /> Educational · Not financial advice
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold font-heading tracking-tight leading-[1.05]">
            Learn what moves a stock, <span className="text-primary">one score at a time.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
            Aqvoro AI scores a curated watchlist 1–100 daily, ranks the top swing picks, explains the reasoning
            in plain English, and lets you paper-trade risk-free. Built for beginners.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" onClick={go} data-testid="landing-cta-main"
              className="h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold gap-2">
              Explore today's picks <ArrowRight className="h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/dashboard")} className="h-12 px-6">
              View leaderboard
            </Button>
          </div>
        </div>
        <div className="relative ss-fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="absolute -inset-4 bg-gradient-to-tr from-primary/20 via-sky-500/10 to-indigo-500/20 blur-3xl rounded-full" />
          <img src="https://images.unsplash.com/photo-1612178991541-b48cc8e92a4d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
            alt="Stock chart analysis" className="relative rounded-2xl border border-border shadow-2xl w-full object-cover aspect-[4/3]" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {FEATURES.map((f, i) => (
            <div key={i} className="rounded-2xl border border-border bg-card p-6 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
              <div className="h-11 w-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-1.5">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-amber-500/20 bg-amber-500/10 p-6 text-center">
          <p className="text-sm text-amber-600 dark:text-amber-400/90 font-medium">
            ⚠️ Aqvoro AI is strictly for education. Scores and picks are not recommendations. No real money is involved.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
